import { Navigate } from "react-router";
import { useIntl } from "react-intl";
import { Bot, LayoutGrid, PauseCircle, PlayCircle, Sparkles } from "lucide-react";
import { toast } from "sonner";

import { CenteredLoader } from "@/components/common";
import { BrandSettingsShell } from "@/components/settings/brand-settings-shell";
import { getUnavailableBrandPath, useRouteBrand } from "@/hooks/use-route-brand";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import { getAssetCreationFlowCopy, getAssetCreationSourceCopy } from "@/pages/brand-settings-state";
import {
  ASSET_CREATION_FLOWS,
  DEFAULT_ASSET_CREATION_FLOW,
  type AssetCreationFlow,
} from "@runcheetah/shared";
import { Badge, Button, Card, CardContent, CardHeader, CardTitle } from "@runcheetah/ui";

export default function BrandSettingsPage() {
  const intl = useIntl();
  const { brand, brandQuery } = useRouteBrand();
  const utils = trpc.useUtils();
  const billingAvailabilityQuery = trpc.brand.getBillingAvailability.useQuery(
    { brandId: brand?.id ?? "" },
    { enabled: Boolean(brand?.id), staleTime: 0 },
  );
  const autopilotQuery = trpc.brand.getAutopilotSetting.useQuery(
    { brandId: brand?.id ?? "" },
    { enabled: Boolean(brand?.id), staleTime: 0 },
  );
  const assetCreationQuery = trpc.brand.getAssetCreationSetting.useQuery(
    { brandId: brand?.id ?? "" },
    { enabled: Boolean(brand?.id), staleTime: 0 },
  );
  const updateAutopilot = trpc.brand.updateAutopilotSetting.useMutation({
    onSuccess: async (_result, variables) => {
      await Promise.all([
        utils.brand.getAutopilotSetting.invalidate({ brandId: variables.brandId }),
        utils.campaign.nextGenerationCheck.invalidate({ brandId: variables.brandId }),
      ]);
    },
  });
  const updateAssetCreation = trpc.brand.updateAssetCreationSetting.useMutation({
    onSuccess: async (result, variables) => {
      utils.brand.getAssetCreationSetting.setData({ brandId: variables.brandId }, result);
      await utils.brand.getAssetCreationSetting.invalidate({ brandId: variables.brandId });
    },
  });

  if (
    brandQuery.isPending ||
    (brand &&
      (autopilotQuery.isPending ||
        assetCreationQuery.isPending ||
        billingAvailabilityQuery.isPending))
  ) {
    return (
      <CenteredLoader
        label={intl.formatMessage({
          id: "brandSettings.loading",
          defaultMessage: "Loading brand settings...",
        })}
      />
    );
  }

  if (!brand || brand.status !== "scanned") {
    return <Navigate to={getUnavailableBrandPath(brand?.id)} replace />;
  }

  const autopilotEnabled = autopilotQuery.data?.enabled ?? true;
  const assetCreationFlow = assetCreationQuery.data?.flow ?? DEFAULT_ASSET_CREATION_FLOW;
  const assetCreationSource = assetCreationQuery.data?.source ?? "default";
  const assetCreationCopy = getAssetCreationFlowCopy(assetCreationFlow);
  const assetCreationSourceCopy = getAssetCreationSourceCopy(assetCreationSource);
  const pendingAssetCreationFlow = updateAssetCreation.isPending
    ? (updateAssetCreation.variables?.flow ?? null)
    : null;
  const billingEnabled = billingAvailabilityQuery.data?.enabled ?? false;
  const copy = autopilotEnabled
    ? {
        eyebrow: intl.formatMessage({
          id: "brandSettings.autopilot.activeEyebrow",
          defaultMessage: "Autopilot is active",
        }),
        title: intl.formatMessage({
          id: "brandSettings.autopilot.activeTitle",
          defaultMessage: "Campaigns generate automatically",
        }),
        description: intl.formatMessage({
          id: "brandSettings.autopilot.activeDescription",
          defaultMessage:
            "Cheetah will keep creating campaign drafts from the active strategy and queue the next run after each cycle.",
        }),
        actionLabel: intl.formatMessage({
          id: "brandSettings.autopilot.turnOff",
          defaultMessage: "Turn off Autopilot",
        }),
      }
    : {
        eyebrow: intl.formatMessage({
          id: "brandSettings.autopilot.pausedEyebrow",
          defaultMessage: "Autopilot is paused",
        }),
        title: intl.formatMessage({
          id: "brandSettings.autopilot.pausedTitle",
          defaultMessage: "Campaign generation is manual",
        }),
        description: intl.formatMessage({
          id: "brandSettings.autopilot.pausedDescription",
          defaultMessage:
            "Cheetah will not start new automatic campaign generation jobs until Autopilot is turned back on.",
        }),
        actionLabel: intl.formatMessage({
          id: "brandSettings.autopilot.turnOn",
          defaultMessage: "Turn on Autopilot",
        }),
      };
  const Icon = autopilotEnabled ? PlayCircle : PauseCircle;

  const handleToggleAutopilot = async () => {
    try {
      await updateAutopilot.mutateAsync({
        brandId: brand.id,
        enabled: !autopilotEnabled,
      });
      toast.success(
        !autopilotEnabled
          ? intl.formatMessage({
              id: "brandSettings.autopilot.turnedOn",
              defaultMessage: "Autopilot turned on",
            })
          : intl.formatMessage({
              id: "brandSettings.autopilot.turnedOff",
              defaultMessage: "Autopilot turned off",
            }),
      );
    } catch {
      toast.error(
        intl.formatMessage({
          id: "brandSettings.autopilot.updateError",
          defaultMessage: "Could not update Autopilot",
        }),
      );
    }
  };

  const handleUpdateAssetCreationFlow = async (flow: AssetCreationFlow) => {
    if (flow === assetCreationFlow) {
      return;
    }

    try {
      await updateAssetCreation.mutateAsync({ brandId: brand.id, flow });
      toast.success(
        intl.formatMessage({
          id: "brandSettings.assetCreation.updated",
          defaultMessage: "Asset creation mode updated",
        }),
      );
    } catch {
      toast.error(
        intl.formatMessage({
          id: "brandSettings.assetCreation.updateError",
          defaultMessage: "Could not update asset creation mode",
        }),
      );
    }
  };

  return (
    <BrandSettingsShell brand={brand} activeSection="autopilot" billingEnabled={billingEnabled}>
      <div className="space-y-6">
        <Card id="autopilot" className="scroll-mt-6 overflow-hidden border-0 bg-card/95 shadow-sm">
          <CardHeader className="bg-muted/20 pb-4">
            <div className="flex items-center gap-3">
              <span className="flex size-10 items-center justify-center rounded-2xl bg-primary text-primary-foreground">
                <Bot className="size-5" />
              </span>
              <div>
                <CardTitle className="text-lg">
                  {intl.formatMessage({
                    id: "brandSettings.autopilot.title",
                    defaultMessage: "Autopilot",
                  })}
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  {intl.formatMessage({
                    id: "brandSettings.autopilot.subtitle",
                    defaultMessage: "Automatic campaign generation",
                  })}
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="grid gap-6 p-5 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center">
            <div className="space-y-4">
              <Badge variant={autopilotEnabled ? "default" : "secondary"} className="w-fit">
                {copy.eyebrow}
              </Badge>
              <div className="flex gap-4">
                <Icon className="mt-1 size-6 shrink-0 text-primary" />
                <div className="space-y-2">
                  <h2 className="text-2xl font-semibold tracking-tight">{copy.title}</h2>
                  <p className="max-w-2xl text-sm leading-6 text-muted-foreground">
                    {copy.description}
                  </p>
                </div>
              </div>
            </div>
            <Button
              type="button"
              variant={autopilotEnabled ? "outline" : "default"}
              onClick={() => void handleToggleAutopilot()}
              disabled={updateAutopilot.isPending}
              aria-pressed={autopilotEnabled}
            >
              {updateAutopilot.isPending
                ? intl.formatMessage({ id: "common.saving", defaultMessage: "Saving..." })
                : copy.actionLabel}
            </Button>
          </CardContent>
        </Card>
        <Card
          id="asset-creation"
          className="scroll-mt-6 overflow-hidden border-0 bg-card/95 shadow-sm"
        >
          <CardHeader className="bg-muted/20 pb-4">
            <div className="flex items-center gap-3">
              <span className="flex size-10 items-center justify-center rounded-2xl bg-primary text-primary-foreground">
                <Sparkles className="size-5" />
              </span>
              <div>
                <CardTitle className="text-lg">
                  {intl.formatMessage({
                    id: "brandSettings.assetCreation.title",
                    defaultMessage: "Asset creation",
                  })}
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  {intl.formatMessage({
                    id: "brandSettings.assetCreation.subtitle",
                    defaultMessage: "Single-asset planning mode",
                  })}
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6 p-5">
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2">
                <Badge
                  variant={assetCreationFlow === "freestyle" ? "default" : "secondary"}
                  className="w-fit"
                >
                  {intl.formatMessage({
                    id:
                      assetCreationFlow === "freestyle"
                        ? "brandSettings.assetCreation.freestyleEyebrow"
                        : "brandSettings.assetCreation.templateEyebrow",
                    defaultMessage: assetCreationCopy.eyebrow,
                  })}
                </Badge>
                <Badge variant={assetCreationSource === "brand" ? "outline" : "secondary"}>
                  {intl.formatMessage({
                    id:
                      assetCreationSource === "brand"
                        ? "brandSettings.assetCreation.sourceBrand"
                        : assetCreationSource === "global"
                          ? "brandSettings.assetCreation.sourceGlobal"
                          : "brandSettings.assetCreation.sourceDefault",
                    defaultMessage: assetCreationSourceCopy,
                  })}
                </Badge>
              </div>
              <div className="flex gap-4">
                {assetCreationFlow === "freestyle" ? (
                  <Sparkles className="mt-1 size-6 shrink-0 text-primary" />
                ) : (
                  <LayoutGrid className="mt-1 size-6 shrink-0 text-primary" />
                )}
                <div className="space-y-2">
                  <h2 className="text-2xl font-semibold tracking-tight">
                    {intl.formatMessage({
                      id:
                        assetCreationFlow === "freestyle"
                          ? "brandSettings.assetCreation.freestyleTitle"
                          : "brandSettings.assetCreation.templateTitle",
                      defaultMessage: assetCreationCopy.title,
                    })}
                  </h2>
                  <p className="max-w-2xl text-sm leading-6 text-muted-foreground">
                    {intl.formatMessage({
                      id:
                        assetCreationFlow === "freestyle"
                          ? "brandSettings.assetCreation.freestyleDescription"
                          : "brandSettings.assetCreation.templateDescription",
                      defaultMessage: assetCreationCopy.description,
                    })}
                  </p>
                </div>
              </div>
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              {ASSET_CREATION_FLOWS.map((flow) => {
                const optionCopy = getAssetCreationFlowCopy(flow);
                const isSelected = flow === assetCreationFlow;
                const isPendingSelection = flow === pendingAssetCreationFlow;
                const isHighlighted = isSelected || isPendingSelection;
                const OptionIcon = flow === "freestyle" ? Sparkles : LayoutGrid;

                return (
                  <button
                    key={flow}
                    type="button"
                    aria-pressed={isSelected}
                    disabled={updateAssetCreation.isPending}
                    onClick={() => void handleUpdateAssetCreationFlow(flow)}
                    className={cn(
                      "rounded-2xl border p-4 text-left transition disabled:cursor-not-allowed disabled:opacity-70",
                      isHighlighted
                        ? "border-primary bg-primary/5 shadow-sm"
                        : "border-border bg-background/70 hover:border-primary/40 hover:bg-muted/40",
                    )}
                  >
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <span
                        className={cn(
                          "flex size-9 items-center justify-center rounded-xl",
                          isHighlighted
                            ? "bg-primary text-primary-foreground"
                            : "bg-muted text-foreground",
                        )}
                      >
                        <OptionIcon className="size-4" />
                      </span>
                      {isSelected || isPendingSelection ? (
                        <Badge variant="outline">
                          {isPendingSelection
                            ? intl.formatMessage({
                                id: "common.saving",
                                defaultMessage: "Saving...",
                              })
                            : intl.formatMessage({
                                id: "brandSettings.assetCreation.current",
                                defaultMessage: "Current",
                              })}
                        </Badge>
                      ) : null}
                    </div>
                    <div className="space-y-1">
                      <h3 className="font-medium text-foreground">
                        {intl.formatMessage({
                          id:
                            flow === "freestyle"
                              ? "brandSettings.assetCreation.freestyleOptionTitle"
                              : "brandSettings.assetCreation.templateOptionTitle",
                          defaultMessage: optionCopy.optionTitle,
                        })}
                      </h3>
                      <p className="text-sm leading-6 text-muted-foreground">
                        {intl.formatMessage({
                          id:
                            flow === "freestyle"
                              ? "brandSettings.assetCreation.freestyleOptionDescription"
                              : "brandSettings.assetCreation.templateOptionDescription",
                          defaultMessage: optionCopy.optionDescription,
                        })}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>

            <p className="text-xs leading-5 text-muted-foreground">
              {intl.formatMessage({
                id: "brandSettings.assetCreation.scopeNote",
                defaultMessage:
                  "This changes new single assets only. Existing generated posts and carousel assets keep their current structure.",
              })}
            </p>
          </CardContent>
        </Card>
      </div>
    </BrandSettingsShell>
  );
}
